import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import ImageUploadComponent from "@/app/components/ui/ImageUploadComponent";
import { useUploadLabReportAPI } from "@/app/hooks/api_hooks/LabReport/useUploadLabReportAPI";
import { useUploadDocumentAPI } from "@/app/hooks/api_hooks/LabReport/useUploadDocumentAPI";
import { useLabReportStatsListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportStatsListAPI";
import { useLabReportFilterListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportFilterListAPI";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ILabReportListState } from "@/app_redux/reducers/slices/auth/lab_report_slice";
import { useNavigate } from "react-router";
import { Trash2, Eye, FileUp, RefreshCw } from "lucide-react";
import RemoveFileConfirmModal from "../RemoveFileConfirmModal";

interface IUploadLabReportModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  requisitionId: string | null;
  invoiceId: string | null;
  uploadModalType: string | null;
  isDayClosing?: boolean;
  testDetails?: any;
  actionType?: string;
}

export interface IUploadImagepayload {
  requisition_id: string;
  report_url: File[] | string[] | string | null;
}

interface IFormValues {
  report_url: (File | string)[] | null;
}

const UploadLabReportModal: React.FC<IUploadLabReportModalProps> = ({
  open,
  setOpen,
  requisitionId,
  invoiceId,
  uploadModalType,
  isDayClosing,
  testDetails,
  actionType
}) => {
  const initialState = useRef(false);
  const cancelButtonRef = useRef(null);
  const { getRouteKey } = useRouter();
  const { customerId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState.state as ICustomerProfileState;
  const { labReportListFilter } = useRouteInfo(getRouteKey("LAB_REPORT", "id")).routeState.state as ILabReportListState;
  const { execute: uploadToS3 } = useUploadDocumentAPI();
  const { execute: uploadLabReport } = useUploadLabReportAPI();

  const fetchStatsList = useLabReportStatsListAPI();
  const { execute: fetchLabReports } = useLabReportFilterListAPI();
  const [isImageRequired, setIsImageRequired] = useState<boolean>(false);
  const isLabTestUrl = location.pathname.includes("lab-test");
  const labTestType = location.pathname.split("/").pop();
  const [loader, setLoader] = useState(false);
  const [hasReplacedFile, setHasReplacedFile] = useState(false);
  const [removedReportIndices, setRemovedReportIndices] = useState<number[]>([]);
  const [originalUrls, setOriginalUrls] = useState<string[]>([]);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [removeIndex, setRemoveIndex] = useState<number | null>(null);

  const formHook = useForm<IFormValues>({
    mode: "onChange",
    defaultValues: { report_url: null },
  });

  const handleFileReplace = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.target?.files && event?.target?.files?.length > 0) {
      const files = Array.from(event.target.files);
      formHook.setValue("report_url", files);
      setHasReplacedFile(true);
      setIsImageRequired(false);
    }
  };

  const handleFileAppend = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.target?.files && event?.target?.files?.length > 0) {
      const files = Array.from(event.target.files);
      const currentValue = formHook.getValues("report_url");
      const currentFiles = Array.isArray(currentValue) ? currentValue : [];
      formHook.setValue("report_url", [...currentFiles, ...files]);
      setHasReplacedFile(true);
      setIsImageRequired(false);
    }
  };

  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";
  const processId = queryParams.get("closing_process");
  const isClinicView = queryParams.get("is_clinic");
  const city_id = queryParams.get("city_id");

  const LabType = (tab: string) => {
    if (tab === "incoming") {
      return 2;
    } else if (tab === "external") {
      return 1;
    } else if (tab === "internal") {
      return 3;
    }
  };

  const onSubmit = async (data: IFormValues) => {
    if (uploadModalType !== "EDIT") {
      formHook.reset();
      setOpen(false);
      return;
    }
    if (!data?.report_url || (Array.isArray(data?.report_url) && data?.report_url?.length === 0)) {
      setIsImageRequired(true);
      return;
    } else {
      setIsImageRequired(false);
    }
    if (typeof data?.report_url === "string") {
      setIsImageRequired(true);
      return;
    }
    setLoader(true);

    const method = actionType === "re_upload_report" ? "put" : "post";

    const response = await uploadLabReport(requisitionId, data?.report_url, method, method === "put" ? removedReportIndices.map(i => i + 1) : []);

    if (response?.status) {
      setRemovedReportIndices([]);
      setOpen(false);
      processId &&
        navigate(
          `/app/closing/day-closing?closing_process=${processId}&city_id=${city_id}&clinic_ids=${queryClinics}&is_clinic=${isClinicView}&shift=${queryShift}&t=${Date.now()}`,
        );
      setLoader(false);
      if (processId) {
        // Day closing: the navigate above bumps `t`, which makes the closing tab
        // refetch its own list. Fetching here too would overwrite it.
      } else if (isLabTestUrl) {
        const constructedBody: any = {
          start_date: labReportListFilter?.start_date,
          end_date: labReportListFilter?.end_date,
          customer_id: customerId,
          type: 3,
        };
        // fetchStatsList(constructedBody);
        fetchLabReports(constructedBody);
      } else {
        let payload: any = {
          start_date: labReportListFilter?.start_date,
          end_date: labReportListFilter?.end_date,
          status_ids: labReportListFilter?.status_ids,
          clinic_ids: labReportListFilter?.clinic_ids,
          lab_test_type: labTestType,
          type: LabType(location.pathname.split("/").pop()),
        };
        if (labReportListFilter?.lab_partner_ids && labReportListFilter?.lab_test_type) {
          payload = {
            ...payload,
            lab_partner_ids: labReportListFilter?.lab_partner_ids,
          };
        }
        fetchStatsList(payload);
        fetchLabReports(payload);
      }
      formHook.reset();
    } else {
      formHook.setValue("report_url", null);
      setHasReplacedFile(false);
    }
    setLoader(false);
  };

  useEffect(() => {
    if (!testDetails?.lab_report_url) return;

    const raw = testDetails.lab_report_url;
    let urls: string[] = [];

    if (Array.isArray(raw)) {
      urls = raw.map((v) => String(v));
    } else {
      urls = [String(raw)];
    }

    setOriginalUrls(urls);
    formHook.setValue("report_url", urls);
    setRemovedReportIndices([]);
    setHasReplacedFile(false);
  }, [testDetails]);


  const HeaderIcon = uploadModalType === "EDIT" ? FileUp : Eye;

  const reportValue = formHook.watch("report_url");
  const reportFiles = Array.isArray(reportValue) ? reportValue.filter((f): f is File => f instanceof File) : [];

  const existingUrls: string[] =
    Array.isArray(reportValue)
      ? reportValue.filter((item): item is string => typeof item === "string")
      : typeof reportValue === "string"
        ? [reportValue]
        : [];


  const hasExistingReport = existingUrls.length > 0;
  const hasSelectedFiles = reportFiles.length > 0;
  const remainingOriginalCount = originalUrls.length - removedReportIndices?.length;
  const totalRemainingFiles = remainingOriginalCount + reportFiles?.length;
  const isUploadDisabled = loader || (uploadModalType === "EDIT" && totalRemainingFiles === 0);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={() => setOpen(false)}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <form
              onSubmit={formHook.handleSubmit(onSubmit)}
              className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle w-full sm:max-w-xl"
            >
              <div className="bg-[#F9FAFB] px-6 py-4 flex items-center justify-between border-b border-gray-100 sticky top-0 z-10">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-black rounded-xl">
                    <HeaderIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-900 leading-tight">
                      {uploadModalType === "EDIT" ? "Upload" : "View"} Lab Report
                    </h2>
                    <p className="text-[11px] text-gray-500 font-medium  tracking-wider mt-0.5">Requisition Report</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="p-1.5 rounded-full hover:bg-gray-200 transition-colors bg-gray-100"
                  onClick={() => {
                    formHook.reset();
                    setOpen(false);
                  }}
                >
                  <XMarkIcon className="h-5 w-5 text-gray-600" />
                </button>
              </div>

              <div className="px-4 py-4">
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100 mb-6 font-primary">

                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-500  tracking-wider">Requisition ID</p>
                    <p className="text-xs font-medium text-gray-900">{testDetails?.requisition_number || "NA"}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-500  tracking-wider">Customer Name</p>
                    <p className="text-xs font-medium text-gray-900">{testDetails?.customer_details?.name || "NA"}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-500  tracking-wider">Pet Details</p>
                    <p className="text-xs font-medium text-gray-900">
                      {testDetails?.pet_details?.name}
                      <span className="text-gray-400 font-normal ml-1">
                        ({testDetails?.pet_details?.pet_species_name?.toLowerCase() === "canine" ? "Dog" : "Cat"})
                      </span>
                    </p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-500  tracking-wider">Lab Name</p>
                    <p className="text-xs font-medium text-gray-900 " title={testDetails?.lab_partner_name}>
                      {testDetails?.lab_partner_name || "NA"}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-500  tracking-wider">Test Name</p>
                    <p className="text-xs font-medium text-gray-900 " title={testDetails?.test_name}>
                      {testDetails?.test_name || "NA"}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-500  tracking-wider">Doctor</p>
                    <p className="text-xs font-medium text-gray-900 capitalize">{testDetails?.staff_details?.name || "NA"}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900">Lab Report Document <span className="text-red-500">*</span></p>
                  </div>

                  <div className="bg-white rounded-xl transition-all hover:border-gray-300">


                    {uploadModalType === "EDIT" && !reportValue && (
                      <ImageUploadComponent
                        handleFileChange={handleFileReplace}
                        text="Click to upload or drag and drop"
                        data={reportValue}
                        required={isImageRequired}
                      />
                    )}

                    {hasExistingReport && (
                      <div className="max-h-[260px] h-auto overflow-y-auto space-y-3">

                        {originalUrls.map((url, originalIndex) => {
                          if (removedReportIndices?.includes(originalIndex)) return null;
                          const fileName = url.split("/").pop();

                          return (
                            <div
                              key={url + originalIndex}
                              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 p-4 bg-gray-50/50 rounded-xl border border-gray-100/50"
                            >
                              <div className="flex items-center gap-4 min-w-0 w-full sm:w-auto">
                                <div className="w-12 h-12 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center justify-center shrink-0">
                                  <img
                                    src="https://vetic-img.s3.ap-south-1.amazonaws.com/quick_commerce//misc/pdf-svgrepo-com.svg"
                                    className="w-8 h-8"
                                  />
                                </div>

                                <div className="flex flex-col min-w-0">
                                  <p className="text-sm font-bold text-gray-900 truncate">
                                    {fileName || `Report ${originalIndex + 1}`}
                                  </p>
                                  <p className="text-[10px] font-medium text-gray-500">
                                    Ready to view
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 shrink-0 w-full justify-end sm:w-auto sm:ml-4">

                                <button
                                  type="button"
                                  className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-50 shadow-sm"
                                  onClick={() => window.open(encodeURI(url), "_blank")}
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                  View
                                </button>

                                {uploadModalType === "EDIT" && (
                                  <button
                                    type="button"
                                    className="flex items-center gap-1.5 px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-xs font-bold text-red-600 hover:bg-red-100 shadow-sm"
                                    onClick={() => {
                                      const remainingOriginal = originalUrls.length - removedReportIndices.length;
                                      const totalRemaining = remainingOriginal + reportFiles.length;
                                      if (totalRemaining <= 0) return;
                                      setRemoveIndex(originalIndex);
                                      setOpenRemoveModal(true);
                                    }}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    Remove
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {hasSelectedFiles && (
                      <div className="max-h-[260px] h-auto overflow-y-auto space-y-3 pr-2">
                        {reportFiles.map((file, index) => (
                          <div
                            key={`${file.name}-${file.lastModified}-${index}`}
                            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 p-4 bg-gray-50/50 rounded-xl border border-gray-100/50 transition-all"
                          >
                            <div className="flex items-center gap-4 min-w-0 w-full sm:w-auto">
                              <div className="w-12 h-12 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center justify-center shrink-0">
                                <img
                                  src="https://vetic-img.s3.ap-south-1.amazonaws.com/quick_commerce//misc/pdf-svgrepo-com.svg"
                                  alt="File Icon"
                                  className="w-8 h-8"
                                />
                              </div>

                              <div className="flex flex-col min-w-0">
                                <p className="text-sm font-bold text-gray-900 truncate" title={file.name}>
                                  {file.name}
                                </p>
                                <p className="text-[10px] font-medium text-gray-500  tracking-tight">Ready to view or manage</p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 shrink-0 w-full justify-end sm:w-auto sm:ml-4">
                              <button
                                type="button"
                                className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-50 transition-all shadow-sm active:scale-95"
                                onClick={() => {
                                  const url = URL.createObjectURL(file);
                                  window.open(url, "_blank");
                                  setTimeout(() => URL.revokeObjectURL(url), 100);
                                }}
                              >
                                <Eye className="w-3.5 h-3.5" />
                                View
                              </button>

                              {uploadModalType === "EDIT" && (
                                <button
                                  type="button"
                                  className="flex items-center gap-1.5 px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-xs font-bold text-red-600 hover:bg-red-100 transition-all shadow-sm active:scale-95"
                                  onClick={() => {
                                    const remainingOriginal = originalUrls.length - removedReportIndices.length;
                                    const totalRemaining = remainingOriginal + reportFiles.length;
                                    if (totalRemaining <= 1) return;
                                    setRemoveIndex(originalUrls?.length + index);
                                    setOpenRemoveModal(true);
                                  }}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/*  GLOBAL ADD MORE BUTTON — ALWAYS LAST */}
                    {uploadModalType === "EDIT" && (
                      <div className="pt-2">
                        <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-50 shadow-sm">
                          <input
                            type="file"
                            className="hidden"
                            multiple
                            onChange={handleFileAppend}
                          />
                          <RefreshCw className="w-3.5 h-3.5 text-gray-400" />
                          Add More
                        </label>
                      </div>
                    )}

                  </div>
                </div>
              </div>

              <div className="px-4 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3 rounded-b-2xl">
                <button
                  type="button"
                  className="px-6 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-bold text-gray-700 hover:bg-gray-50 transition-all"
                  onClick={() => {
                    formHook.reset();
                    setOpen(false);
                  }}
                >
                  Cancel
                </button>
                {uploadModalType === "EDIT" ? (
                  <button
                    type="submit"
                    disabled={isUploadDisabled}
                    className={`px-10 py-2.5 rounded-xl text-sm font-bold text-white transition-all flex items-center justify-center min-w-[140px] ${isUploadDisabled
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-black hover:opacity-90"
                      }`}
                  >
                    {loader ? (
                      <div className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        <span>Uploading...</span>
                      </div>
                    ) : (
                      "Upload Report"
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="px-10 py-2.5 rounded-xl bg-black text-sm font-bold text-white hover:opacity-90 transition-all"
                    onClick={() => setOpen(false)}
                  >
                    Done
                  </button>
                )}
              </div>
              <RemoveFileConfirmModal
                open={openRemoveModal}
                setOpen={setOpenRemoveModal}
                loading={false}
                onConfirm={() => {
                  if (removeIndex !== null) {
                    if (removeIndex < originalUrls.length) {
                      setRemovedReportIndices(prev =>
                        prev.includes(removeIndex) ? prev : [...prev, removeIndex]
                      );
                      setHasReplacedFile(true);
                    }
                    else {
                      const fileIndex = removeIndex - originalUrls.length;
                      const nextFiles = reportFiles.filter((_, i) => i !== fileIndex);
                      formHook.setValue("report_url", nextFiles.length > 0 ? nextFiles : null);
                      if (nextFiles.length === 0) {
                        setHasReplacedFile(false);
                      }
                    }
                  }

                  setRemoveIndex(null);
                  setOpenRemoveModal(false);
                }}
              />
            </form>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default UploadLabReportModal;
